/**
 * RollbackDialog — modal de confirmacion para rollback (ADR_012 PR-CMgmt-G).
 *
 * Reutilizable en dos contextos:
 *   - Rollback individual: profileId omitido, targetLabel = device.role
 *   - Rollback masivo: profileId presente, targetLabel = nombre del perfil +
 *     "(N equipos)"
 *
 * UX:
 *   - 6 inputs separados para el codigo TOTP (autofocus en el primero)
 *   - Boton "Confirmar" disabled hasta que esten los 6 digitos
 *   - Errores del backend se muestran en banda roja al pie del modal
 *   - El campo notas es opcional (max 500 char)
 *
 * El componente NO sabe de api/queries — solo expone onConfirm({code, notes}).
 * Las paginas que lo usan crean el hook y conectan el callback.
 */
import { useEffect, useRef, useState } from "react";
import { AlertTriangle, X } from "lucide-react";

interface RollbackDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (args: { totpCode: string; notes: string }) => void;
  isSubmitting: boolean;
  errorMessage: string | null;
  /** Texto del target a revertir: ej. "Cosmos principal_entrada" o "Bio Hikvision Holanda · 107 equipos". */
  targetLabel: string;
  /** Texto auxiliar bajo el target (opcional). Ej: "Vuelve al snapshot known_good". */
  helperText?: string;
  /**
   * Titulo del modal. Por defecto el de rollback, para no tocar los usos que ya
   * existian. Lo usa tambien el candado de pausar un perfil de cosecha, que pide
   * el mismo segundo factor.
   */
  title?: string;
  /** Texto del boton de confirmar. Mismo motivo que `title`. */
  confirmLabel?: string;
}

export function RollbackDialog({
  open,
  onClose,
  onConfirm,
  isSubmitting,
  errorMessage,
  targetLabel,
  helperText,
  title = "Confirmar rollback",
  confirmLabel = "Confirmar rollback",
}: RollbackDialogProps) {
  const [digits, setDigits] = useState<string[]>(["", "", "", "", "", ""]);
  const [notes, setNotes] = useState("");
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  // Reset al abrir/cerrar
  useEffect(() => {
    if (open) {
      setDigits(["", "", "", "", "", ""]);
      setNotes("");
      // Autofocus primer input despues de un tick
      setTimeout(() => inputsRef.current[0]?.focus(), 0);
    }
  }, [open]);

  if (!open) return null;

  const totpCode = digits.join("");
  const isComplete = totpCode.length === 6 && /^\d{6}$/.test(totpCode);

  const handleDigit = (idx: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[idx] = digit;
    setDigits(next);
    if (digit && idx < 5) inputsRef.current[idx + 1]?.focus();
  };

  const handleKeyDown = (
    idx: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      e.preventDefault();
      setDigits(pasted.split(""));
      inputsRef.current[5]?.focus();
    }
  };

  const handleSubmit = () => {
    if (!isComplete || isSubmitting) return;
    onConfirm({ totpCode, notes: notes.trim() });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rollback-title"
    >
      <div
        className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="size-5 text-amber-600" />
            <h2 id="rollback-title" className="text-lg font-semibold">
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted"
            aria-label="Cerrar"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="mb-4 rounded-md border bg-muted/30 p-3 text-sm">
          <div className="font-medium">{targetLabel}</div>
          {helperText && (
            <div className="mt-1 text-xs text-muted-foreground">
              {helperText}
            </div>
          )}
        </div>

        <p className="mb-2 text-sm text-muted-foreground">
          Ingresa el código de 6 dígitos de tu app de autenticación:
        </p>

        <div className="mb-4 flex gap-2" role="group" aria-label="Código TOTP">
          {digits.map((d, idx) => (
            <input
              key={idx}
              ref={(el) => {
                inputsRef.current[idx] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => handleDigit(idx, e.target.value)}
              onKeyDown={(e) => handleKeyDown(idx, e)}
              onPaste={handlePaste}
              className="size-10 rounded-md border bg-background text-center font-mono text-lg focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              aria-label={`Dígito ${idx + 1}`}
              disabled={isSubmitting}
            />
          ))}
        </div>

        <label className="mb-4 block text-sm">
          <span className="mb-1 block text-muted-foreground">
            Notas (opcional)
          </span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 500))}
            placeholder="Motivo del rollback, ticket, etc."
            rows={2}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            disabled={isSubmitting}
          />
        </label>

        {errorMessage && (
          <div
            role="alert"
            className="mb-4 rounded-md border border-red-500/30 bg-red-500/10 p-2 text-sm text-red-700 dark:text-red-400"
          >
            {errorMessage}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-md border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isComplete || isSubmitting}
            className="rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? "Aplicando…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
